// MA-831 — the ONE write path for a child's progress record.
//
// Before this module App.jsx kept two copies of a child's progress (React
// state and the localStorage record) and every write decided on ONE copy and
// overwrote the other:
//  - `setProgress` ran the caller's updater on React's in-memory `prev` and
//    saved the result over the whole stored record. SkillTree,
//    useTodaysMission and ParentSettings write the stored record directly and
//    never touch React state, so the kid's next concept acknowledgement or a
//    missions reset saved stale React state over an offline assign.
//  - the save went to the render's closure child id, so an updater that ran
//    after a KidSwitcher switch could save one child's state into the other
//    child's key.
//  - writeJSON swallowed a quota failure, so the stored record silently fell
//    behind React state, and the next cloud re-merge (which decides on the
//    stored record, MA-395) rolled back stars, stories and a level change.
//
// This writer is synchronous (no React updater: StrictMode's double invoke and
// lane rebasing never re-run a write) and does a read-modify-write on the
// stored record, keyed by an explicit child id:
//  - base = the stored record overlaid on React state when React state belongs
//    to that child (readMergeBase: stored keys win, React-only keys survive,
//    nothing stored -> React state), else the stored record over defaults();
//    then every key whose last write for that child FAILED takes the value of
//    the last attempted record (the pending overlay), so a detected failed
//    write never rolls back. The overlay applies to a key only while its
//    stored value is still the one snapshotted at failure time: a later
//    successful direct write (SkillTree) to that key is newer and wins
//    (MA-831 RED round 1 SHOULD-1).
//  - React state is updated only when the id is the LIVE child at call time.
//    A write for another child touches only that child's key.
//
// deps:
//   loadRaw(id)          the raw stored record or null (no defaults)
//   save(id, record)     true when stored, false when the write failed
//   defaults()           a fresh default progress record
//   getLiveChildId()     the live active child id (App's synchronous ref)
//   setReactValue(value) hands a progress VALUE to React (never an updater)
//   initialValue / initialChildId  the React value at creation
import { readMergeBase } from "./cloudMergeGuard.js";

const isRecord = (v) => !!v && typeof v === "object" && !Array.isArray(v);
const sameJSON = (a, b) => a === b || JSON.stringify(a) === JSON.stringify(b);

export function createProgressWriter({
  loadRaw, save, defaults, getLiveChildId, setReactValue,
  initialValue = null, initialChildId = null,
}) {
  let view = { childId: initialChildId ?? null, value: initialValue };
  // childId -> { keys: Set<string>, record, storedAt: { [key]: storedJSON } }
  // for writes that did not land.
  const pending = new Map();

  function setValue(value, childId) {
    // A VALUE only: an updater here would reach React as a functional update
    // and desync `view` from React state. Use write() for updaters.
    if (typeof value === "function") throw new TypeError("progressWriter.setValue takes a progress value, not an updater; use write()");
    view = { childId: childId ?? null, value };
    setReactValue(value);
  }

  function storedOrDefaults(id) {
    const s = loadRaw(id);
    return isRecord(s) ? { ...defaults(), ...s } : defaults();
  }

  function baseFor(id) {
    if (!id) return view.value;
    let base = view.childId === id
      ? readMergeBase(view.value, { mergeChildId: id, liveChildId: id, loadRaw })
      : storedOrDefaults(id);
    const p = pending.get(id);
    if (p) {
      const now = loadRaw(id);
      const s = isRecord(now) ? now : {};
      base = { ...base };
      for (const k of p.keys) {
        if (JSON.stringify(s[k]) !== p.storedAt[k]) continue; // a newer write stored this key
        if (k in p.record) base[k] = p.record[k];
        else delete base[k];
      }
    }
    return base;
  }

  // A VALUE (not an updater) was built by the caller from React state, e.g.
  // `{ ...app.progress, mascot }`. When React state belongs to this child,
  // apply only what the caller changed against it onto the fresh base, so a
  // stored-only edit is never overwritten by the rest of the stale copy.
  function rebaseValue(id, base, value) {
    if (!id || view.childId !== id || !isRecord(value) || !isRecord(view.value)) return value;
    const out = { ...base };
    for (const k of Object.keys(value)) if (value[k] !== view.value[k]) out[k] = value[k];
    for (const k of Object.keys(view.value)) if (!(k in value)) delete out[k];
    return out;
  }

  // The attempted record already carries every earlier pending value (its base
  // had the overlay), so the pending set is rebuilt from it: every key where it
  // differs from what is stored now, with that stored value snapshotted.
  function markPending(id, record) {
    const stored = loadRaw(id);
    const s = isRecord(stored) ? stored : {};
    const keys = new Set();
    const storedAt = {};
    for (const k of new Set([...Object.keys(record), ...Object.keys(s)])) {
      if (!sameJSON(record[k], s[k])) { keys.add(k); storedAt[k] = JSON.stringify(s[k]); }
    }
    if (keys.size) pending.set(id, { keys, record, storedAt });
    else pending.delete(id);
  }

  function persist(id, record) {
    const ok = save(id, record) !== false;
    if (ok) pending.delete(id);
    else markPending(id, record);
    return ok;
  }

  // write(id, updaterOrValue) -> { value, ok, changed }
  function write(id, input) {
    const base = baseFor(id);
    const next = typeof input === "function" ? input(base) : rebaseValue(id, base, input);
    if (!id) {
      // No active child: React-only, nothing to persist (pre-MA-831 behaviour).
      if (next !== view.value) setValue(next, null);
      return { value: next, ok: true, changed: next !== base };
    }
    if (next === base) return { value: base, ok: !pending.has(id), changed: false };
    const ok = persist(id, next);
    if (id === getLiveChildId()) setValue(next, id);
    return { value: next, ok, changed: true };
  }

  // Retry the last record that failed to store for this child.
  function flush(id) {
    const p = id ? pending.get(id) : null;
    if (!p) return true;
    return persist(id, p.record);
  }

  return {
    write,
    setProgress: (input) => write(getLiveChildId(), input),
    setValue,
    baseFor,
    flush,
    hasPendingWrite: (id) => pending.has(id),
    forget: (id) => { pending.delete(id); },
    getView: () => view,
  };
}
